import React, { useState } from 'react';
import { RefreshCw, Image as ImageIcon, Clock, CheckCircle } from 'lucide-react';
import CachedImage from '../components/CachedImage';
import useImagePreloader from '../hooks/useImagePreloader';

const ImageCacheTest = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [loadTimes, setLoadTimes] = useState({});
  const { getCacheStats, clearCache } = useImagePreloader();

  // Sample images for testing
  const testImages = [
    '/images/logo/logo.jpg',
    'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&h=400&fit=crop&crop=center',
    'https://images.unsplash.com/photo-1518717758536-85ae29035b6d?w=400&h=400&fit=crop&crop=center',
    'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=400&fit=crop&crop=center',
    'https://images.unsplash.com/photo-1581092795442-8d6b6b6b6b6b?w=400&h=400&fit=crop&crop=center',
  ];

  const handleImageLoad = (src) => {
    const endTime = Date.now();
    const startTime = loadTimes[src]?.start;
    if (startTime) {
      setLoadTimes(prev => ({
        ...prev,
        [src]: {
          ...prev[src],
          end: endTime,
          duration: endTime - startTime
        }
      }));
    }
  };

  const handleImageStart = (src) => {
    setLoadTimes(prev => ({
      ...prev,
      [src]: {
        start: Date.now(),
        end: null,
        duration: null
      }
    }));
  };

  const pages = [
    {
      id: 1,
      title: 'Page 1 - Logo & Products',
      images: testImages.slice(0, 3)
    },
    {
      id: 2,
      title: 'Page 2 - More Products',
      images: testImages.slice(2, 5)
    },
    {
      id: 3,
      title: 'Page 3 - Mixed Content',
      images: [testImages[0], testImages[3], testImages[1]]
    }
  ];

  const currentPageData = pages.find(p => p.id === currentPage);
  const stats = getCacheStats();

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Image Cache Test</h1>
          <p className="text-gray-600 mb-6">
            Navigate between pages to test image caching. Images should load instantly on subsequent visits.
          </p>
          
          {/* Cache Stats */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6 inline-block">
            <h3 className="text-lg font-semibold mb-2 flex items-center">
              <ImageIcon className="mr-2" size={20} />
              Cache Statistics
            </h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-gray-600">Cached Images</div>
                <div className="font-bold text-blue-600">{stats.cacheSize}/{stats.maxCacheSize}</div>
              </div>
              <div>
                <div className="text-gray-600">Loading</div>
                <div className="font-bold text-orange-600">{stats.loadingPromises}</div>
              </div>
              <div>
                <button
                  onClick={clearCache}
                  className="bg-red-500 text-white px-3 py-1 rounded text-xs hover:bg-red-600 transition-colors"
                >
                  Clear Cache
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page Navigation */}
        <div className="flex justify-center space-x-4 mb-8">
          {pages.map(page => (
            <button
              key={page.id}
              onClick={() => {
                setCurrentPage(page.id);
                // Track load start times for new page
                page.images.forEach(img => handleImageStart(img));
              }}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                currentPage === page.id
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {page.title}
            </button>
          ))}
        </div>

        {/* Current Page Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-2xl font-bold mb-6">{currentPageData.title}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {currentPageData.images.map((src, index) => {
              const loadTime = loadTimes[src];
              return (
                <div key={`${currentPage}-${index}`} className="space-y-3">
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                    <CachedImage
                      src={src}
                      alt={`Test image ${index + 1}`}
                      className="w-full h-full object-cover"
                      onLoad={() => handleImageLoad(src)}
                      placeholder={
                        <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
                          <div className="text-gray-400">Loading...</div>
                        </div>
                      }
                    />
                  </div>
                  
                  {/* Load Time Display */}
                  <div className="text-sm space-y-1">
                    <div className="font-medium text-gray-700">Image {index + 1}</div>
                    {loadTime && (
                      <div className="flex items-center space-x-2">
                        {loadTime.duration !== null ? (
                          <>
                            <CheckCircle size={14} className="text-green-500" />
                            <span className="text-green-600">
                              Loaded in {loadTime.duration}ms
                            </span>
                          </>
                        ) : (
                          <>
                            <Clock size={14} className="text-orange-500" />
                            <span className="text-orange-600">Loading...</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-900 mb-2">How to Test:</h3>
          <ol className="list-decimal list-inside text-blue-800 space-y-1">
            <li>Navigate between pages and observe load times</li>
            <li>Return to a previously visited page - images should load instantly</li>
            <li>Check the cache statistics to see cached images count</li>
            <li>Clear cache and notice the difference in load times</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default ImageCacheTest;
